/* eslint-disable max-statements */
import {useEffect, useState, useCallback, useRef} from 'react';
import classNames from 'classnames';
import {throttle} from 'lodash-es';

import {EDITOR_MODE} from '@/Constant';
import {TITLE_MAX_LENGTH, DEFAULT_TITLE, ERROR_TIP} from '@/constants/document/title';
import {focusToEditor as rteFocusToEditor, disableToolbar as rteDisableToolbar} from '@/rte/editor';
import sdk from '@/sdk';
import toast, {ToastType} from '@/components/Toast';
import {useEditableElementPaste} from '@/domains/document/hooks/title';
import {initGray, GRAY_RELEASE_KEYS} from '@/grayRelease';
import {isInKuShare} from '@/utils/urlUtils';

import AIRecommendTitle from './AIRecommendTitle';
import AddCover from './AddCover';
import ThemeCover from './ThemeCover';
import RePaginate from './RePaginate';

import styles from './index.module.scss';

const {
    useIsInGray: useIsInGrayAiRecommendTitle,
} = initGray(GRAY_RELEASE_KEYS.aiRecommendTitle);

export interface IProps {
    mode: EDITOR_MODE;
    curDocument: WizDocument;
    repository: Repository;
    updateDoTitle: (title?: string) => void;
    hasEditPermission?: boolean;
    isNew?: boolean;
    isCardView?: boolean;
    titleNode?: any;
}

const ENTER_KEY_CODE = 13;
const LEFT_KEY_CODE = 39;

const DocTitle: React.FC<IProps> = ({
    mode,
    curDocument,
    repository,
    updateDoTitle,
    hasEditPermission,
    isNew,
    isCardView,
    titleNode = {},
}) => {
    const {docGuid, name: docTitle = ''} = curDocument;
    const isEditMode = mode === EDITOR_MODE.edit;
    const isNormalMode = mode === EDITOR_MODE.normal;
    const isMeetingMode = mode === EDITOR_MODE.meeting;
    const showEdit = hasEditPermission && (isEditMode || isMeetingMode || isNormalMode);

    const [inputText, setInputText] = useState(docTitle);
    const [value, setValue] = useState('');
    const titleRef = useRef<HTMLDivElement>(null);
    const titleCompareRef = useRef<HTMLDivElement>(null);

    // 兼容火狐浏览器 contentEditable element 粘贴的格式问题
    useEditableElementPaste(titleRef.current);

    useEffect(() => {
        const defaultValue =
            isNew &&
            (isEditMode || isNormalMode || (isMeetingMode && !window.location.search.includes('clientKuShareNew=1')))
                ? ''
                : docTitle;
        setInputText(defaultValue);
        setValue(defaultValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, docTitle, isEditMode, curDocument]); // 注意，这里isNew变更不影响defaultValue取值(否则会造成新建文档时输入内容，标题闪现)

    useEffect(() => {
        docTitle && setValue(docTitle);
    }, [docTitle]);

    const disableToolbar = useCallback((isDisable: boolean) => {
        rteDisableToolbar(isDisable);
    }, []);

    const focusToEditor = useCallback((createNewLine: boolean) => {
        rteFocusToEditor({createNewLine});
        disableToolbar(false);
    }, [disableToolbar]);

    const handleSave = useCallback(async () => {
        disableToolbar(false);
        // 标题为空失焦，不请求接口同步
        if (!value) {
            return;
        }
        // 标题无修改失焦，不重复请求接口
        if (value === curDocument.name) {
            return;
        }
        try {
            const ksServer = await sdk.getKsServer(repository);
            // const newDoc: DocumentUpdateData = {
            //     action: DocumentOperateType.Rename,
            //     name: value.trim() || DEFAULT_TITLE,
            // };
            // const document = await ksServer.updateDocument(docGuid as string, newDoc);

            // MARKS: action = rename改为单独接口
            const titleName = value.trim() || DEFAULT_TITLE;
            const document = await ksServer.renameDocument(docGuid, titleName);
            // 处理重复提交空标题curDocument判断不了变更，导致标题暂时显示空的情况
            if (document.name && document.name !== value) {
                const titleDom: any = titleRef.current;
                titleDom && (titleDom.innerText = document.name);
                setValue(document.name);
            }
        } catch (error) {
            console.error(error);
            setValue(curDocument.name as string); // 重置标题

            toast({
                content: ERROR_TIP,
                type: ToastType.Error,
            }).catch(err => {
                console.log(err);
            });
        }
    }, [disableToolbar, value, curDocument.name, repository, docGuid]);

    const handleChange = useCallback((e: any) => {
        // 注意：safari下存在兼容性问题 (chrome没有问题，稳的一)
        // 问题：在删除标题的情况下，e.target.textContent会得到正常的空字符串""，但是e.target.innerText会得到带换行字符的字符串，长度为1
        // 原因：猜测原因是 safari 对contenteditable div处理存在问题，在删除标题时，会自动插入一个<br />元素
        // 解决：使用textContent，并在删除标题后需要手动清空safari自动插入的 <br /> 元素，才能显示placeholder
        const text = e.target.textContent;
        if (!text) {
            e.target.innerText = '';
        }

        const newTitle = text.slice(0, TITLE_MAX_LENGTH);

        // 字数超出处理
        if (text.length >= TITLE_MAX_LENGTH) {
            e.target.innerText = newTitle;
            const range = window.getSelection();
            range?.selectAllChildren(e.target); // 选中target下所有子内容
            range?.collapseToEnd(); // 光标移至最后
        }

        setValue(newTitle);
        updateDoTitle(newTitle);
    }, [updateDoTitle]);

    const handleKeyDown = useCallback((e: any) => {
        // 处理回车事件，避免跟其它快捷键冲突
        if (!e.metaKey && !e.ctrlKey && e.keyCode === ENTER_KEY_CODE) {
            e.target.blur();
            e.preventDefault();
            e.stopPropagation();
            focusToEditor(true);
        }
        // 支持光标从标题左移进入正文
        if (e.keyCode === LEFT_KEY_CODE) {
            const selection = window.getSelection();
            if (
                (selection && selection.anchorOffset === value.length) ||
                (selection && selection.anchorNode === titleRef.current && selection.anchorOffset === 1)
            ) {
                e.preventDefault();
                e.stopPropagation();
                focusToEditor(false);
            }
        }
    }, [focusToEditor, value.length]);

    const [isFocus, setIsFocus] = useState(false);
    const focusLineEnd = useCallback(() => {
        if (titleRef.current) {
            const titleEl = titleRef.current;
            const selection = window.getSelection();
            selection?.selectAllChildren(titleEl);
            selection?.collapseToEnd();
        }
    }, []);

    const setTitle = useCallback((title: string) => {
        setValue(title);
        if (titleRef.current) {
            titleRef.current.innerText = title;
        }
        focusLineEnd();
        updateDoTitle(title);
    }, [focusLineEnd, updateDoTitle]);

    function onFocus() {
        setIsFocus(true);
        disableToolbar(true);
        rteFocusToEditor({focusTitle: true});
    }

    function onBlur() {
        setIsFocus(false);
        handleSave();
    }

    const [isHover, setIsHover] = useState(false);
    const isKuShare = isInKuShare();
    const isInGray = useIsInGrayAiRecommendTitle();
    const showAiRecommendTitle = isInGray && !isKuShare;
    // 弹窗显示的时候阻止onMouseLeave事件执行。
    const [showPopup, setShowPopup] = useState(false);

    /**
     * 以下逻辑用于卡片视图下标题自适应缩放，该逻辑新需求已去除，暂时注释

    // 计算标题文字缩放
    const requestAnimationFrame = useCallback(() => {
        // 非卡片模式，不进行缩放
        if (!isCardView) {
            return;
        }
        let titleDom = document.querySelector('#documentTitleDiv');
        const sliderCardHeaderDom = titleDom && titleDom.parentElement;
        if (titleDom && isCardView) {
            const meatureDom = sliderCardHeaderDom!.querySelector('#titleCompareDiv');
            if (meatureDom && isCardView) {
                // Note: 此处使用 || 是因为，真 title 的更新早于 measure 因此二者只要有一个大于，则就进行缩放
                if (meatureDom.clientHeight > 88 || titleDom.clientHeight > 88) {
                    titleDom = document.querySelector('#documentTitleDiv');
                    (titleDom as HTMLElement).style.fontSize = `${titleDom!.clientWidth * 0.058}%`;
                }
                else {
                    titleDom = titleDom = document.querySelector('#documentTitleDiv');
                    (titleDom as HTMLElement).style.fontSize = '100%';
                }
            }
        }
    }, [isCardView]);

    useEffect(() => {
        // 监听虚拟title宽度变化，调整id为'documentTitleDiv'（预览态和编辑态的dom，ID都是documentTitleDiv）的font-size
        let resizeObserver: ResizeObserver | null = null;
        if (window.ResizeObserver && titleCompareRef.current) {
            resizeObserver = new window.ResizeObserver(throttle(requestAnimationFrame, 500));

            resizeObserver.observe(titleCompareRef.current);
        }

        return () => {
            resizeObserver && resizeObserver.disconnect();
        };
    }, [requestAnimationFrame]);

    useEffect(() => {
        requestAnimationFrame();
    }, [showEdit, inputText, value, requestAnimationFrame]);

    */

    // 判断光标是否在titleRef
    const isCursorInside = () => {
        const selection = window.getSelection();
        if (!selection?.rangeCount) {
            return false;
        }

        const range = selection.getRangeAt(0);
        return titleRef.current?.contains(range.commonAncestorContainer);
    };

    // 点击标题后空白处，光标也需要能focus
    const handleWrapperClick = () => {
        if (titleRef.current && !isCursorInside()) {
            focusLineEnd();
        }
    };

    return curDocument && (
        <div
            id="documentTitleRoot"
            className={classNames(styles.root, isCardView && styles.cardView, showEdit && styles.editCursor)}
            onMouseOver={() => !isHover && setIsHover(true)}
            onMouseLeave={() => isHover && !showPopup && setIsHover(false)}
            onClick={handleWrapperClick}
            style={{textAlign: titleNode.textAlign}}
        >
            {
                showEdit
                    ? <>
                        <div className={styles.titleWrapper}>
                            <div
                                id="documentTitleDiv"
                                ref={titleRef}
                                contentEditable
                                suppressContentEditableWarning
                                spellCheck={false} // 禁用拼写检查
                                className={
                                    classNames(
                                        styles.title,
                                        styles.edit,
                                        isCardView && styles.cardViewColor
                                    )
                                } // edit样式会影响contentEditable的判断，需要分开
                                onInput={handleChange}
                                onKeyDown={handleKeyDown}
                                onBlur={onBlur}
                                onFocus={onFocus}
                                data-placeholder={curDocument.name || '请输入文档标题'}
                            >
                                {inputText}
                            </div>
                        </div>
                        <div className={styles.titleTop}>
                            <ThemeCover
                                isHover={isHover}
                                docGuid={docGuid}
                                isCardView={isCardView}
                                setShowPopup={setShowPopup}
                                setIsHover={setIsHover}
                            />
                            <RePaginate
                                isHover={isHover}
                                docGuid={docGuid}
                                isCardView={isCardView}
                            />
                            <AddCover
                                isHover={isHover}
                                docGuid={docGuid}
                                isCardView={isCardView}
                            />
                            {showAiRecommendTitle && <AIRecommendTitle
                                title={value}
                                setTitle={setTitle}
                                isFocus={isFocus}
                                isHover={isHover}
                                docGuid={docGuid}
                            />}
                        </div>
                    </>
                    : (
                        <div className={styles.titleWrapper}>
                            <div
                                id="documentTitleDiv"
                                className={classNames(styles.title, isCardView && styles.cardViewColor)}
                                style={{textAlign: titleNode.textAlign}}
                            >
                                {inputText}
                            </div>
                        </div>
                    )
            }
            {/* 以下 div 用于卡片视图下标题自适应缩放，该逻辑新需求已去除，暂时注释 */}
            {/* <div
                id="titleCompareDiv"
                ref={titleCompareRef}
                className={classNames(styles.title, styles.titleCompareDiv)}
            >
                {value}
            </div> */}
        </div>
    );
};

export default DocTitle;
